// ./src/components/generic/AttachedFiles/AttachedFiles.tsx
import React, { useMemo, useCallback } from "react";
import {
  Box,
  Text,
  Button,
  Group,
  SimpleGrid,
  Collapse,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
  DraggableRubric,
} from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";

import { UploadedFile } from "@/utils/api/UploadedFileService";

import { AppIcon } from "../AppIcon";
import { FilePreviewCard } from "../FilePreviewCard";

// ============================================
// Types
// ============================================

export interface AttachedFilesProps {
  /** List of attached files to display */
  files: UploadedFile[];

  /** Callback when a file should be removed */
  onRemove?: (fileId: string) => void;

  /** Callback when add button is clicked */
  onAddClick?: () => void;

  /** Callback when files are reordered */
  onReorder?: (reorderedFiles: UploadedFile[]) => void;

  /** Whether the component is in readonly mode (no add/remove/reorder) */
  readonly?: boolean;

  /** Maximum number of files to display before showing "show more" */
  maxDisplay?: number;

  /** Custom message when no files are attached */
  emptyMessage?: string;

  /** Custom label for the add button */
  addButtonLabel?: string;

  /** Grid columns configuration */
  gridColumns?: {
    base: number;
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };

  /** Card size variant */
  cardSize?: "sm" | "md" | "lg";

  /** Whether to show as a collapsible section */
  collapsible?: boolean;

  /** Section title (used when collapsible) */
  title?: string;

  /** Whether section is initially expanded (when collapsible) */
  defaultExpanded?: boolean;

  /** Whether drag-and-drop reordering is enabled */
  reorderable?: boolean;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_GRID_COLUMNS = { base: 2, xs: 2, sm: 3, md: 4, lg: 5 };
const DEFAULT_MAX_DISPLAY = 10;

// ============================================
// Helper function to reorder array
// ============================================

const reorderArray = <T,>(
  list: T[],
  startIndex: number,
  endIndex: number
): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// ============================================
// Component
// ============================================

export const AttachedFiles: React.FC<AttachedFilesProps> = ({
  files,
  onRemove,
  onAddClick,
  onReorder,
  readonly = false,
  maxDisplay = DEFAULT_MAX_DISPLAY,
  emptyMessage,
  addButtonLabel,
  gridColumns = DEFAULT_GRID_COLUMNS,
  cardSize = "md",
  collapsible = false,
  title,
  defaultExpanded = true,
  reorderable = false,
}) => {
  const { t } = useTranslation(["common"]);
  const theme = useMantineTheme();

  // State for collapsible section
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  // State for showing all files
  const [showAll, setShowAll] = React.useState(false);

  // Determine which files to display
  const displayedFiles = useMemo(() => {
    if (showAll || files.length <= maxDisplay) {
      return files;
    }
    return files.slice(0, maxDisplay);
  }, [files, maxDisplay, showAll]);

  // Count of hidden files
  const hiddenCount = files.length - displayedFiles.length;

  // Check if we have files
  const hasFiles = files.length > 0;

  // Check if reordering is enabled and applicable
  const canReorder = reorderable && !readonly && onReorder && files.length > 1;

  // Toggle expanded state
  const handleToggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  // Toggle show all
  const handleToggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

  // Handle drag end
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || !onReorder) {
        return;
      }

      // If dropped in the same position, do nothing
      if (result.destination.index === result.source.index) {
        return;
      }

      const reorderedFiles = reorderArray(
        files,
        result.source.index,
        result.destination.index
      );

      onReorder(reorderedFiles);
    },
    [files, onReorder]
  );

  // Render section header (for collapsible mode)
  const renderSectionHeader = () => {
    if (!collapsible) return null;

    return (
      <UnstyledButton onClick={handleToggleExpanded} w="100%">
        <Group justify="space-between" py="xs">
          <Group gap="xs">
            <Text fw={600} size="sm">
              {title || t("common:attachments.title")}
            </Text>
            {files.length > 0 && (
              <Text size="xs" c="dimmed">
                ({files.length})
              </Text>
            )}
          </Group>
          <AppIcon
            iconName={expanded ? "ChevronUp" : "ChevronDown"}
            size={16}
          />
        </Group>
      </UnstyledButton>
    );
  };

  // Render empty state
  const renderEmptyState = () => {
    if (hasFiles) return null;

    return (
      <Box py="md" ta="center">
        <AppIcon
          iconName="FileX"
          size={32}
          style={{ color: "var(--mantine-color-gray-4)" }}
        />
        <Text size="sm" c="dimmed" mt="xs">
          {emptyMessage || t("common:attachments.noDocuments")}
        </Text>
      </Box>
    );
  };

  // Render a single file card
  const renderFileCard = (
    file: UploadedFile,
    isDragging?: boolean,
    dragHandleProps?: DraggableProvided["dragHandleProps"]
  ) => (
    <FilePreviewCard
      file={file}
      onRemove={onRemove}
      showRemove={!readonly && !!onRemove}
      size={cardSize}
      readonly={readonly}
      isDragging={isDragging}
      dragHandleProps={canReorder ? dragHandleProps : undefined}
      showDragHandle={canReorder}
    />
  );

  // Render clone for dragging (rendered in portal, doesn't affect layout)
  const renderClone = useCallback(
    (
      provided: DraggableProvided,
      _: DraggableStateSnapshot,
      rubric: DraggableRubric
    ) => {
      const file = displayedFiles[rubric.source.index];
      return (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
          }}
        >
          {renderFileCard(file, true, provided.dragHandleProps)}
        </Box>
      );
    },
    [displayedFiles, cardSize, canReorder, onRemove, readonly]
  );

  // Render files grid with drag-and-drop
  const renderDraggableFilesGrid = () => {
    if (!hasFiles) return null;

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable
          droppableId="attached-files"
          direction="horizontal"
          renderClone={renderClone}
        >
          {(provided: DroppableProvided) => (
            <SimpleGrid
              cols={gridColumns}
              spacing="sm"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {displayedFiles.map((file, index) => (
                <Draggable
                  key={file.id || index}
                  draggableId={file.id || `file-${index}`}
                  index={index}
                >
                  {(
                    dragProvided: DraggableProvided,
                    snapshot: DraggableStateSnapshot
                  ) => (
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      style={{
                        ...dragProvided.draggableProps.style,
                        // When dragging, this is the placeholder - keep it visible but dimmed
                        opacity: snapshot.isDragging ? 0.4 : 1,
                        // Reset transform for the placeholder so it stays in place
                        transform: snapshot.isDragging
                          ? "none"
                          : dragProvided.draggableProps.style?.transform,
                      }}
                    >
                      {/* Show a placeholder outline when this item is being dragged */}
                      {snapshot.isDragging ? (
                        <Box
                          style={{
                            border: `2px dashed ${theme.colors.blue[4]}`,
                            borderRadius: theme.radius.md,
                            backgroundColor: theme.colors.blue[0],
                            height: "100%",
                            minHeight:
                              cardSize === "sm"
                                ? 140
                                : cardSize === "md"
                                  ? 170
                                  : 200,
                          }}
                        />
                      ) : (
                        renderFileCard(
                          file,
                          false,
                          dragProvided.dragHandleProps
                        )
                      )}
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </SimpleGrid>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  // Render files grid without drag-and-drop
  const renderStaticFilesGrid = () => {
    if (!hasFiles) return null;

    return (
      <SimpleGrid cols={gridColumns} spacing="sm">
        {displayedFiles.map((file) => (
          <Box key={file.id}>{renderFileCard(file)}</Box>
        ))}
      </SimpleGrid>
    );
  };

  // Render files grid (chooses between draggable and static)
  const renderFilesGrid = () => {
    if (!hasFiles) return null;

    return (
      <Box>
        {canReorder ? renderDraggableFilesGrid() : renderStaticFilesGrid()}

        {/* Show more/less button */}
        {files.length > maxDisplay && (
          <Box mt="sm" ta="center">
            <Button
              variant="subtle"
              size="xs"
              onClick={handleToggleShowAll}
              leftSection={
                <AppIcon
                  iconName={showAll ? "ChevronUp" : "ChevronDown"}
                  size={14}
                />
              }
            >
              {showAll
                ? t("common:attachments.showLess")
                : t("common:attachments.showMore", { count: hiddenCount })}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  // Render add button
  const renderAddButton = () => {
    if (readonly || !onAddClick) return null;

    return (
      <Button
        variant="light"
        size="sm"
        leftSection={<AppIcon iconName="Plus" size={16} />}
        onClick={onAddClick}
      >
        {addButtonLabel || t("common:attachments.addDocument")}
      </Button>
    );
  };

  // Render reorder hint
  const renderReorderHint = () => {
    if (!canReorder) return null;

    return (
      <Text size="xs" c="dimmed" mb="xs">
        {t("common:attachments.dragToReorder", {
          defaultValue: "Drag files to reorder",
        })}
      </Text>
    );
  };

  // Main content
  const content = (
    <Box>
      {/* Reorder hint */}
      {renderReorderHint()}

      {/* Files grid or empty state */}
      {renderFilesGrid()}
      {renderEmptyState()}

      {/* Add button */}
      {!readonly && onAddClick && (
        <Box mt={hasFiles ? "md" : "xs"}>{renderAddButton()}</Box>
      )}
    </Box>
  );

  // Render with or without collapsible wrapper
  if (collapsible) {
    return (
      <Box>
        {renderSectionHeader()}
        <Collapse in={expanded}>{content}</Collapse>
      </Box>
    );
  }

  return content;
};
